/**
 * The vocabulary of the platform, as a closed set of keys.
 *
 * These are the words a community is most likely to have its own version of. They are separated
 * from ordinary interface strings because they behave differently: a translation is chosen by
 * locale, a *term* is chosen by community, and the two are independent — a community may run
 * the English interface while calling a competition a "special" and a participant a "dancer".
 *
 * Both a singular and a plural are stored for each. English pluralisation by suffix breaks on
 * exactly the words a community is most likely to replace, and a community writing in
 * Anishinaabemowin does not pluralise by adding an "s" at all.
 */

export interface Term {
  one: string;
  other: string;
}

export const DEFAULT_TERMS = {
  event: { one: "Event", other: "Events" },
  session: { one: "Session", other: "Sessions" },
  grandEntry: { one: "Grand Entry", other: "Grand Entries" },
  competition: { one: "Competition", other: "Competitions" },
  category: { one: "Category", other: "Categories" },
  phase: { one: "Round", other: "Rounds" },
  participant: { one: "Participant", other: "Participants" },
  dancerNumber: { one: "Dancer number", other: "Dancer numbers" },
  registration: { one: "Registration", other: "Registrations" },
  judge: { one: "Judge", other: "Judges" },
  arenaDirector: { one: "Arena Director", other: "Arena Directors" },
  mc: { one: "MC", other: "MCs" },
  elder: { one: "Elder", other: "Elders" },
  drum: { one: "Drum", other: "Drums" },
  honorarium: { one: "Honorarium", other: "Honoraria" },
  prize: { one: "Prize", other: "Prizes" },
  payout: { one: "Payout", other: "Payouts" },
  checkIn: { one: "Check-in", other: "Check-ins" },
  community: { one: "Community", other: "Communities" },
  exhibition: { one: "Exhibition", other: "Exhibitions" },
  special: { one: "Special", other: "Specials" },
} as const satisfies Record<string, Term>;

export type TermKey = keyof typeof DEFAULT_TERMS;

/**
 * The same vocabulary in French.
 *
 * A locale default and a community override are different things and both have to exist. Choosing
 * French must not silently rename a competition back to "Competition", and a community that calls
 * a participant a *danseur* must still be able to say so on top of the French defaults — so this
 * is the base layer, and `terminology_json` still wins over it.
 *
 * `mc` and `grandEntry` stay as they are. They are what is said aloud in the arena in both
 * languages, and translating them would be a correction nobody asked for.
 */
export const FR_TERMS = {
  event: { one: "Événement", other: "Événements" },
  session: { one: "Séance", other: "Séances" },
  grandEntry: { one: "Grand Entry", other: "Grand Entries" },
  competition: { one: "Compétition", other: "Compétitions" },
  category: { one: "Catégorie", other: "Catégories" },
  phase: { one: "Ronde", other: "Rondes" },
  participant: { one: "Participant", other: "Participants" },
  dancerNumber: { one: "Numéro de danseur", other: "Numéros de danseur" },
  registration: { one: "Inscription", other: "Inscriptions" },
  judge: { one: "Juge", other: "Juges" },
  arenaDirector: { one: "Directeur d’arène", other: "Directeurs d’arène" },
  mc: { one: "MC", other: "MC" },
  elder: { one: "Aîné", other: "Aînés" },
  drum: { one: "Tambour", other: "Tambours" },
  honorarium: { one: "Honoraires", other: "Honoraires" },
  prize: { one: "Prix", other: "Prix" },
  payout: { one: "Versement", other: "Versements" },
  checkIn: { one: "Arrivée", other: "Arrivées" },
  community: { one: "Communauté", other: "Communautés" },
  exhibition: { one: "Démonstration", other: "Démonstrations" },
  special: { one: "Spécial", other: "Spéciaux" },
} as const satisfies Record<TermKey, Term>;

/**
 * What is stored in `communities.terminology_json`. Partial by design — a community overrides
 * the two or three words it cares about, not all of them.
 *
 * A bare string is accepted as shorthand for "both forms are this word", which is the common
 * case for a word that does not inflect. Anishinaabemowin generally does not pluralise the way
 * the `one`/`other` pair assumes, and forcing a community to write the same word twice to say
 * so would be a small insult repeated on every screen.
 */
export type TermOverrides = Partial<Record<TermKey, Partial<Term> | string>>;
